import Chip from 'core-system/Chip'
import HorizontalScroller from 'core-system/HorizontalScroller'
import pxToRem from 'core-system/utils/pxToRem'
import {
  Incentives as IncentivesIcon,
  Leaderboards as LeaderboardsIcon,
} from 'core-system/Icons/Sidebar'
import Text from 'core-system/Text'
import palette from 'core-system/Themes/palette'
import LeaderboardUtils from 'features/Leaderboards/LeaderboardUtils'
import { CompetitionData, IncentiveData } from 'redux/employee/employeeTypes'
import React from 'react'
import FormattingUtils from 'shared/FormattingUtils'
import styled from 'styled-components'
import {
  buildCompetitionRows,
  buildIncentiveRows,
  commuterGroupNames,
  EngagementReward,
  EngagementRow,
  EngagementSegment,
  oneTimeRewardsEarned,
} from './commuterEngagement'

const Section = styled.div`
  margin-top: 1rem;
`

const Card = styled.div`
  flex: 0 0 18.5rem;
  margin-right: 0.75rem;
  padding: 0.75rem;
  border: 1px solid ${(props) => props.theme.palette.grey.grey300};
  border-radius: 0.5rem;
  background-color: ${(props) => props.theme.palette.white};
`

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
`

const PillRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
  margin-top: 0.75rem;
  pointer-events: none;
`

const ItemList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 0.75rem;
`

const ItemRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
`

const IconWell = styled.div`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 0.3125rem;
  background-color: ${(props) => props.theme.palette.secondary.purple200};
  border: 1px solid ${(props) => props.theme.palette.secondary.purple300};
`

const ItemCopy = styled.div`
  min-width: 0;
  flex: 1;
`

const ItemTitle = styled(Text)`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const EmptyText = styled(Text)`
  margin-top: 0.75rem;
`

interface CommuterGroupsEngagementProps {
  currentDate: string
  employeeId: string
  segmentsEnrolled?: string[]
  segmentsById?: Record<string, EngagementSegment> | null
  incentiveData?: IncentiveData[]
  competitionData?: CompetitionData[]
  oneTimeRewards?: EngagementReward[] | null
}

const RewardRows = ({
  rows,
  icon,
}: {
  rows: EngagementRow[]
  icon: React.ReactNode
}) => {
  if (rows.length === 0) {
    return (
      <EmptyText variant='body2' textColor={palette.text.secondary}>
        None
      </EmptyText>
    )
  }
  return (
    <ItemList>
      {rows.map((row) => (
        <ItemRow key={row.id}>
          <IconWell>{icon}</IconWell>
          <ItemCopy>
            <ItemTitle variant='action4'>{row.title}</ItemTitle>
            {row.caption ? (
              <Text variant='caption' textColor={palette.text.secondary}>
                {row.caption}
              </Text>
            ) : null}
          </ItemCopy>
          <Text variant='action4'>
            {FormattingUtils.formatDollar(row.amountCents)}
          </Text>
        </ItemRow>
      ))}
    </ItemList>
  )
}

const CommuterGroupsEngagement = (props: CommuterGroupsEngagementProps) => {
  const groups = commuterGroupNames(props.segmentsEnrolled, props.segmentsById)
  const incentives = buildIncentiveRows(
    props.incentiveData,
    props.segmentsById,
    props.currentDate,
    oneTimeRewardsEarned(
      props.oneTimeRewards,
      props.employeeId,
      props.currentDate
    )
  )
  const competitions = buildCompetitionRows(
    props.competitionData,
    props.currentDate,
    (queryType) => {
      const titles = LeaderboardUtils.leaderboardData as Record<
        string,
        { smallTitle: string } | undefined
      >
      return titles[queryType]?.smallTitle || queryType
    }
  )

  return (
    <Section>
      <Text
        variant='caption'
        textColor={palette.text.secondary}
        letterSpacing='0.04em'
        style={{ textTransform: 'uppercase' }}
      >
        Groups & Engagement
      </Text>
      <HorizontalScroller
        translationPerClick={312}
        opacityWidth={pxToRem(48)}
        marginTop='0.75rem'
      >
        <Card>
          <CardHeader>
            <Text variant='action3'>Groups</Text>
          </CardHeader>
          {groups.length === 0 ? (
            <EmptyText variant='body2' textColor={palette.text.secondary}>
              None
            </EmptyText>
          ) : (
            <PillRow>
              {groups.map((name, index) => (
                <Chip key={`${name}-${index}`} variant='filter'>
                  {name}
                </Chip>
              ))}
            </PillRow>
          )}
        </Card>
        <Card>
          <CardHeader>
            <Text variant='action3'>Incentives</Text>
            <Text variant='caption' textColor={palette.text.secondary}>
              Earned
            </Text>
          </CardHeader>
          <RewardRows
            rows={incentives}
            icon={
              <IncentivesIcon
                width={16}
                height={16}
                color={palette.text.secondary}
              />
            }
          />
        </Card>
        <Card>
          <CardHeader>
            <Text variant='action3'>Competitions</Text>
            <Text variant='caption' textColor={palette.text.secondary}>
              Earned
            </Text>
          </CardHeader>
          <RewardRows
            rows={competitions}
            icon={
              <LeaderboardsIcon
                width={16}
                height={16}
                color={palette.text.secondary}
              />
            }
          />
        </Card>
      </HorizontalScroller>
    </Section>
  )
}

export default CommuterGroupsEngagement
